import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Box,
  FormLabel,
} from '@chakra-ui/react';
import { SelectField } from '../SelectField';
import { TextGroupField } from '../Text/TextGroup';

export const PhoneField = forwardRef(({
  id,
  name,
  type,
  onBlur,
  options,
  showCode,
  codeValue,
  labelText,
  codeError,
  onCodeBlur,
  isDisabled,
  numberValue,
  placeholder,
  numberError,
  onNumberBlur,
  onCodeChange,
  onNumberChange,
  isSelectCodeDisabled,
  'data-testid': dataTestId,
  ...contentProps
}, ref) => {
  const codeName = `${name}Code`;
  const numberName = `${name}Number`;
  const codeTestId = dataTestId ? `${dataTestId}-code` : null;
  const gridColumn = showCode ? '2' : '0';
  const leftContent = showCode ? `+${codeValue}` : null;

  return (
    <Grid
      columnGap="1rem"
      templateColumns="auto 1fr"
      {...contentProps}
    >
      {showCode && (
        <>
          <FormLabel
            // to be HTML valid, the label must be associated with the input
            // but we don't want to show it following the design
            display="none"
            htmlFor={codeName}
          >
            {/* Render codeName since cannot be modified externally */}
            {codeName}
          </FormLabel>
          <SelectField
            width="66px"
            id={codeName}
            gridArea="2 / 1"
            name={codeName}
            value={codeValue}
            error={codeError}
            onBlur={onCodeBlur}
            onChange={onCodeChange}
            data-testid={codeTestId}
            inputProps={{ fontSize: '16px' }}
            isDisabled={isSelectCodeDisabled || isDisabled}
          >
            {options.map(({ key, value, description }) => (
              <Box
                key={key}
                as="option"
                color="black"
                value={value}
              >
                {description}
              </Box>
            ))}
          </SelectField>
        </>
      )}
      <FormLabel
        htmlFor={numberName}
        gridColumn="1 / -1"
      >
        {labelText}
      </FormLabel>
      <TextGroupField
        ref={ref}
        id={numberName}
        type={type}
        name={numberName}
        value={numberValue}
        error={numberError}
        onBlur={onNumberBlur}
        isDisabled={isDisabled}
        gridColumn={gridColumn}
        data-testid={dataTestId}
        onChange={onNumberChange}
        placeholder={placeholder}
        leftContent={leftContent}
        autoComplete="tel-national"
      />
    </Grid>
  );
});

PhoneField.defaultProps = {
  id: '',
  labelText: '',
  codeValue: '',
  showCode: true,
  placeholder: '',
  numberValue: '',
  type: 'telephone',
  'data-testid': '',
  isSelectCodeDisabled: false,
  onCodeBlur: () => {},
  onCodeChange: () => {},
  onNumberBlur: () => {},
  onNumberChange: () => {},
};

PhoneField.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  showCode: PropTypes.bool,
  onCodeBlur: PropTypes.func,
  labelText: PropTypes.string,
  codeValue: PropTypes.string,
  onCodeChange: PropTypes.func,
  onNumberBlur: PropTypes.func,
  placeholder: PropTypes.string,
  numberValue: PropTypes.string,
  onNumberChange: PropTypes.func,
  'data-testid': PropTypes.string,
  name: PropTypes.string.isRequired,
  isSelectCodeDisabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  })).isRequired,
};
